import { cn } from '@/lib/utils';
import { ErrorIndicator } from '@/components/ui/indicator';
import { motion } from 'framer-motion';
import { Check, Circle } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface StepComponent<TState = unknown> {
  onContinue: () => void;
  onBack?: () => void;
  state?: TState;
  setState: (state: TState) => void;
  prerequisite?: () => boolean;
}

export interface Step<TProps = Record<string, unknown>, TState = unknown> {
  id: string;
  labelKey: string;
  description?: string;
  component?: React.ComponentType<StepComponent<TState> & TProps>;
  validation?: unknown;
  props?: TProps;
  prerequisite?: () => boolean;
}

interface StepNavigationProps {
  steps: Step[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick: (stepIndex: number) => void;
  stepsWithErrors?: Set<number>;
  className?: string;
  variant?: 'default' | 'bullet' | 'vertical';
  /** When true, all steps appear clickable (no disabled/blocked styling) */
  allowAll?: boolean;
  /** Furthest step reached in the wizard; enables back/forward within visited steps in create mode */
  maxReachedStep?: number;
}

const ProcessBar = ({ currentStep, steps, onStepClick }: { currentStep: number; steps: Step[]; onStepClick: (stepIndex: number) => void }) => (
  <div className="relative w-full scale-75">
    <div className="flex items-center justify-between">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center" onClick={() => onStepClick(index)}>
            <motion.div
              className={`z-10 flex h-6 w-6 items-center justify-center rounded-full ${
                index <= currentStep
                  ? 'bg-brand-secondary text-white'
                  : 'bg-muted text-muted-foreground'
              }`}
              animate={{ scale: 1.02 }}
            >
              {index < currentStep ? (
                <Check size={17} />
              ) : (
                <Circle size={17} fill="currentColor" />
              )}
            </motion.div>
          </div>
          {index < steps.length - 1 && (
            <div className="relative grow">
              <div className="absolute -top-1 h-1.5 w-full bg-muted" />
              <motion.div
                className="absolute -top-1 h-1.5 w-full bg-brand-primary"
                initial={{ width: '0%' }}
                animate={{
                  width: index < currentStep ? '100%' : '0%',
                }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

export function StepNavigation({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  stepsWithErrors,
  className,
  variant = 'default',
  allowAll = false,
  maxReachedStep,
}: StepNavigationProps) {
  const { t } = useTranslation();

  if (variant === 'bullet') {
    return <ProcessBar currentStep={currentStep} steps={steps} onStepClick={onStepClick} />;
  }

  const isClickable = (index: number) =>
    allowAll
    || (maxReachedStep !== undefined
      ? index <= maxReachedStep
      : index <= currentStep || completedSteps.has(index - 1));

  if (variant === 'vertical') {
    return (
      <nav className={cn('mt-2 flex w-full shrink-0 flex-col gap-1 lg:w-44', className)}>
        {steps.map((step, index) => {
          const hasError = stepsWithErrors?.has(index);
          const isActive = index === currentStep;
          const showErrorIndicator = index < currentStep && !!hasError;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => isClickable(index) && onStepClick(index)}
              className={cn(
                'rounded-md border px-4 py-2 text-left font-label transition-all',
                isActive
                  ? 'border-brand-secondary bg-brand-secondary text-white'
                  : 'border-transparent bg-muted text-muted-foreground hover:bg-muted/80',
                !isClickable(index) && 'cursor-not-allowed opacity-50',
              )}
            >
              <span className="flex items-center gap-1.5">
                {`${index + 1}. ${t(step.labelKey)}`}
                <ErrorIndicator visible={showErrorIndicator} />
              </span>
            </button>
          );
        })}
      </nav>
    );
  }

  return (
    <div className={cn('mb-6', className)}>
      <div className="flex gap-2 overflow-x-auto">
        {steps.map((step, index) => {
          const hasError = stepsWithErrors?.has(index);
          const showErrorIndicator = index < currentStep && !!hasError;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => onStepClick(index)}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 font-label transition-colors',
                index === currentStep
                  ? 'bg-brand-secondary text-white'
                  : completedSteps.has(index)
                  ? 'bg-success-bg text-success'
                  : 'bg-muted text-muted-foreground',
                isClickable(index)
                  ? 'cursor-pointer hover:opacity-90'
                  : 'cursor-not-allowed opacity-50'
              )}
            >
              {`${index + 1}. ${t(step.labelKey)}`}
              <ErrorIndicator visible={showErrorIndicator} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
